import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '../helpers/cn';

type SeatTooltipProps = {
    message: string | null;
    position: { x: number; y: number };
};

const AUTO_DISMISS_MS = 2000;

const SeatTooltip: React.FC<SeatTooltipProps> = ({ message, position }) => {
    const [visible, setVisible] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        if (message) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: tie tooltip visibility lifecycle to a timer triggered by message changes
            setVisible(true);
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                setVisible(false);
            }, AUTO_DISMISS_MS);
        } else {
            setVisible(false);
        }

        return () => clearTimeout(timerRef.current);
    }, [message]);

    if (!message) return null;

    return (
        <div
            className={cn(
                'absolute pointer-events-none z-10',
                'bg-darkBlue/80 text-white text-body-14 rounded-[4px] px-8 py-4',
                'whitespace-nowrap'
            )}
            style={{
                left: position.x,
                top: position.y,
                transform: 'translate(-50%, calc(-100% - 8px))',
                transition: visible ? 'opacity 150ms ease-in' : 'opacity 300ms ease-out',
                opacity: visible ? 1 : 0
            }}>
            {message}
        </div>
    );
};

export default SeatTooltip;
